import { apiFetch } from '../lib/api';
import type { Market } from '../types';

// Holiday definition for our app
export interface Holiday {
  date: string; // YYYY-MM-DD
  name: string;
  market: Market;
}

// Backend API response types
interface BuiltinHolidaysResponse {
  canada: { date: string; name: string }[];
  usa: { date: string; name: string }[];
}

interface CustomHoliday {
  id: string;
  date: string;
  name: string;
  market: Market | 'both';
  type: 'statutory' | 'custom' | 'blackout';
}

interface HolidaySettingsResponse {
  warningDays: number;
  customHolidays: CustomHoliday[];
}

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

const CACHE_KEY = 'ts-holiday-cache';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface CachedHolidays {
  holidays: Holiday[];
  fetchedAt: number;
}

// Load cached holidays from localStorage
function loadCached(): CachedHolidays | null {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as CachedHolidays;
      // Check if cache is still valid
      if (Date.now() - parsed.fetchedAt < CACHE_TTL_MS) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Failed to load holiday cache:', e);
  }
  return null;
}

// Save holidays to localStorage cache
function saveCache(holidays: Holiday[]): void {
  try {
    const cache: CachedHolidays = {
      holidays,
      fetchedAt: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.error('Failed to save holiday cache:', e);
  }
}

// Fetch builtin statutory holidays from backend
async function fetchBuiltinHolidays(): Promise<Holiday[]> {
  const response = await apiFetch(`${API_BASE_URL}/api/settings/holidays/builtin`);
  if (!response.ok) {
    throw new Error(`Builtin holidays API returned ${response.status}`);
  }
  const data: BuiltinHolidaysResponse = await response.json();

  const holidays: Holiday[] = [];

  for (const h of data.canada) {
    holidays.push({ date: h.date, name: h.name, market: 'canada' });
  }
  for (const h of data.usa) {
    holidays.push({ date: h.date, name: h.name, market: 'usa' });
  }

  return holidays;
}

// Fetch custom holidays from backend
async function fetchCustomHolidays(): Promise<Holiday[]> {
  const response = await apiFetch(`${API_BASE_URL}/api/settings/holidays`);
  if (!response.ok) {
    throw new Error(`Custom holidays API returned ${response.status}`);
  }
  const data: HolidaySettingsResponse = await response.json();

  const holidays: Holiday[] = [];

  for (const h of data.customHolidays) {
    if (h.market === 'both') {
      holidays.push({ date: h.date, name: h.name, market: 'canada' });
      holidays.push({ date: h.date, name: h.name, market: 'usa' });
    } else {
      holidays.push({ date: h.date, name: h.name, market: h.market });
    }
  }

  return holidays;
}

// Fetch all holidays from backend APIs (builtin + custom)
export async function fetchAllHolidays(): Promise<Holiday[]> {
  // Check cache first
  const cached = loadCached();
  if (cached) {
    return cached.holidays;
  }

  const [builtin, custom] = await Promise.all([
    fetchBuiltinHolidays(),
    fetchCustomHolidays(),
  ]);

  const allHolidays = [...builtin, ...custom];

  // Sort by date
  const sorted = allHolidays.sort((a, b) => a.date.localeCompare(b.date));

  // Cache the results
  saveCache(sorted);

  return sorted;
}

// Get all holidays (uses cache for synchronous access, returns empty array if not cached yet)
let holidayPromise: Promise<Holiday[]> | null = null;

export function getAllHolidays(): Holiday[] {
  const cached = loadCached();
  if (cached) {
    return cached.holidays;
  }
  return [];
}

// Initialize holidays (call on app start)
export async function initializeHolidays(): Promise<Holiday[]> {
  if (!holidayPromise) {
    holidayPromise = fetchAllHolidays().catch((e) => {
      console.error('Failed to fetch holidays from backend:', e);
      // Return cached data if available (even if expired), otherwise empty
      try {
        const stored = localStorage.getItem(CACHE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as CachedHolidays;
          return parsed.holidays;
        }
      } catch {
        // ignore parse errors
      }
      return [];
    });
  }
  return holidayPromise;
}

// Get upcoming holidays within N days
export function getUpcomingHolidays(daysAhead: number = 7): Holiday[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() + daysAhead);

  return getAllHolidays()
    .filter((h) => {
      const holidayDate = new Date(h.date + 'T00:00:00');
      return holidayDate >= today && holidayDate <= cutoff;
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

// Get days until a holiday
export function getDaysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T00:00:00');
  const diffMs = target.getTime() - today.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

// Format days until as human readable
export function formatDaysUntil(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}
